import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from "react-oidc-context";
import { useAuthStatus } from "./AuthStatusContext";

const AuthRoute: React.FC = () => {
  const { authStatus } = useAuthStatus();
  const auth = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (auth.isAuthenticated && auth.user?.access_token) {
        try {
          const tokenData = JSON.parse(atob(auth.user.access_token.split('.')[1]));
          const now = Math.floor(Date.now() / 1000);
          setIsValidToken(tokenData.exp > now);
        } catch (err) {
          setIsValidToken(false);
        }
      } else {
        setIsValidToken(false);
      }
    };

    validateToken();
  }, [auth.isAuthenticated, auth.user?.access_token]);

  if (authStatus === "unauth" && !auth.isLoading) {
    if (location.pathname.startsWith("/oauth2")) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.custom.gradients.primary,
            padding: 2
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              maxWidth: 400,
              width: '100%',
              textAlign: 'center',
              borderRadius: theme.custom.spacing.borderRadius
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: theme.custom.typography.weights.bold,
                color: theme.palette.primary.main,
                marginBottom: 2
              }}
            >
              MentoringFusion
            </Typography>
            <CircularProgress sx={{ mb: 2, color: theme.palette.primary.main }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: theme.custom.typography.weights.medium,
                marginBottom: 1
              }}
            >
              Completing Sign In
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Please wait while we verify your authentication...
            </Typography>
          </Paper>
        </Box>
      );
    }
    else {
      return <Navigate to="/signin" replace />;
    }
  }

  // Loading states
  if (auth.isLoading || isValidToken === null || authStatus === "loading") {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Loading MentoringFusion...
        </Typography>
      </Box>
    );
  }

  // Not authenticated
  if (!auth.isAuthenticated || authStatus === "unauth" || isValidToken === false) {
    return <Navigate to="/signin" replace />;
  }

  return <Outlet />;
};

export default AuthRoute;
